import {Fragment, memo} from 'react'
import cn from 'clsx'
import {Line} from '@ant-design/plots'
import {Space, Divider} from 'antd'

import styles from './CashflowForecastChart.module.scss'
import useStore from 'base/store'
import {ProfileRole} from 'modules/users'
import {useOpen} from 'common/hooks'
import {
  Box,
  IconStat,
  Text,
  Tooltip,
  Modal,
  MoneyIcon,
  CashflowIcon,
  RotateArrowFourthIcon,
  RotateArrowThirdIcon,
} from 'common/components'
import {formatMoney, maybePluralize, isEqual, formatDate} from 'common/helpers'
import {COLOR, CHART_THEME, CASHFLOW_CHART_MONTHS_COUNT} from 'common/constants'
import {CashflowForecastValue, CashflowMonthDetails} from '..'

interface CashflowForecastChartProps {
  cahsflowForecast: any
  data: CashflowForecastValue[]
}

interface ChartProps {
  role: ProfileRole
  cahsflowForecast: any
  data: CashflowForecastValue[]
}

interface ColumnProps {
  cahsflowForecast: any
  value: CashflowForecastValue
}

interface ChartLineProps {
  config: any
}

interface TooltipContentProps {
  role: ProfileRole
  value: CashflowForecastValue
}

export default function CashflowForecastChart({
  cahsflowForecast,
  data,
}: CashflowForecastChartProps) {
  const role = useStore((state) => state.role)

  const startValue = data[0]
  const endValue = data[CASHFLOW_CHART_MONTHS_COUNT - 1]
  const icon = role.manager ? <CashflowIcon /> : <MoneyIcon />

  return (
    <>
      <div className={styles.root}>
        <div className={styles.block}>
          <Text weight="medium">
            {role.manager ? 'Forecasted cashflow total' : 'Available fund'} for
            upcoming {CASHFLOW_CHART_MONTHS_COUNT - 1} months
          </Text>
          <IconStat
            icon={icon}
            value={formatMoney(Math.abs(startValue.amount), {
              parentheses: startValue.amount > 0,
            })}
            label={
              role.manager
                ? `${startValue.month} cashflow total`
                : 'Open available fund'
            }
          />
        </div>
        <Chart role={role} cahsflowForecast={cahsflowForecast} data={data} />
        <div className={styles.block}>
          <IconStat
            icon={icon}
            align="right"
            value={formatMoney(Math.abs(endValue.amount), {
              parentheses: endValue.amount > 0,
            })}
            label={
              role.manager
                ? 'Forecasted cashflow total'
                : 'Forecasted available fund'
            }
          />
        </div>
      </div>
    </>
  )
}

const Chart = ({role, cahsflowForecast, data}: ChartProps) => {
  const dataAmount = data.map((value) => value.amount)
  const minAmount = Math.min(...dataAmount)
  const maxAmount = Math.max(...dataAmount)
  const percentAmount = Math.abs(maxAmount - minAmount) / 100
  const zeroBottomPercent = !role.manager
    ? minAmount < 0
      ? Math.abs(minAmount) / percentAmount
      : null
    : null

  const config: any = {
    ...options,
    data,
    yAxis: {
      label: false,
      minLimit: minAmount,
      maxLimit: maxAmount,
      grid: {
        line: false,
      },
    },
    point: {
      size: 5.5,
      style: (value: any) => {
        const isFirstColumn = value.month === data[0].month
        return {
          fill: isFirstColumn ? COLOR.coal : COLOR.white,
          stroke: COLOR.coal,
          lineWidth: isFirstColumn ? 1 : 3,
        }
      },
    },
  }

  return (
    <>
      <div className={styles.chart}>
        <div className={styles.columns}>
          {data.map((value) => (
            <div
              key={value.month}
              className={cn(
                styles.column,
                role.manager && value.empty && styles.column_empty
              )}
              style={{
                paddingTop: columnPadding,
                paddingBottom: columnPadding,
              }}
            >
              <Box fadeIn className={styles.columnContent}>
                {zeroBottomPercent && (
                  <div
                    className={styles.positiveBlock}
                    style={{
                      height: `calc(100% - ${zeroBottomPercent}% + ${
                        columnPadding + 1
                      }px)`,
                      top: -columnPadding - 1,
                    }}
                  />
                )}
                {!value.empty && value.diffType && (
                  <Label
                    value={value}
                    minAmount={minAmount}
                    percentAmount={percentAmount}
                  />
                )}
              </Box>
              <Text
                spacing
                uppercase
                className={styles.month}
                color="atmo"
                size="2xs"
                weight="bold"
                align="center"
              >
                {value.month}
              </Text>
            </div>
          ))}
        </div>
        {zeroBottomPercent && (
          <div
            className={styles.zeroWrapper}
            style={{height: `calc(100% - ${columnPadding * 2}px)`}}
          >
            <div
              className={styles.zeroLine}
              style={{bottom: `${zeroBottomPercent}%`}}
            />
          </div>
        )}
        <ChartLine config={config} />
        <div className={styles.columns}>
          {data.map((value) => (
            <Column
              key={value.month}
              cahsflowForecast={cahsflowForecast}
              value={value}
            />
          ))}
        </div>
      </div>
      <Tooltip
        id={tooltipChartId}
        getContent={(month) => (
          <TooltipContent
            role={role}
            value={data.find((value) => value.month === month)}
          />
        )}
      />
    </>
  )
}

const Column = ({cahsflowForecast, value}: ColumnProps) => {
  const [isOpen, handleOpen, handleClose] = useOpen()

  return (
    <>
      <div
        className={cn(styles.column, styles.column_tooltip)}
        data-for={value.empty ? undefined : tooltipChartId}
        data-tip={value.empty ? undefined : value.month}
        onClick={handleOpen}
      />
      <Modal
        narrow
        title={`Cashflow forecast details for ${formatDate(
          value.monthDate,
          'eight'
        )}`}
        width={1320}
        open={isOpen}
        onCancel={handleClose}
      >
        <CashflowMonthDetails
          value={value}
          cahsflowForecast={cahsflowForecast}
        />
      </Modal>
    </>
  )
}

const ChartLine = memo(
  ({config}: ChartLineProps) => <Line {...config} />,
  (prevProps, nextProps) => isEqual(prevProps.config, nextProps.config)
)

const Label = ({value, minAmount, percentAmount}: any) => {
  if (!value.diffType) return null
  const isRepayment = value.diffType === 'repayment'
  const diffAmount = Math.abs(value.totalDiff)
  const diffPercent = (value.prevAmount - minAmount) / percentAmount
  const lineHeightPercent = diffAmount / percentAmount
  const lineBottom = isRepayment
    ? `${diffPercent - lineHeightPercent}%`
    : `${diffPercent}%`
  const textBottom = isRepayment
    ? `calc(${diffPercent}% + ${labelTextRepaymentOffset}px`
    : `calc(${diffPercent}% - ${labelTextHeight + labelTextDrawdownOffset}px)`

  return (
    <div className={cn(styles.label, styles[`label_${value.diffType}`])}>
      <div
        className={styles.labelLine}
        style={{bottom: lineBottom, height: `${lineHeightPercent}%`}}
      />
      <Text
        className={styles.labelText}
        style={{bottom: textBottom, height: labelTextHeight}}
      >
        <span>
          {formatMoney(diffAmount / 1000, {
            prefix: false,
          })}
          k
        </span>
      </Text>
    </div>
  )
}

const TooltipContent = ({role, value}: TooltipContentProps) => {
  if (!value) return null

  const positivePrev = value.prevAmount > 0
  const positive = value.amount > 0
  const label = role.manager ? 'Forcasted cashflow' : 'Available fund'

  return (
    <>
      <div className={styles.tooltipBlock}>
        <Text weight="bold">{formatDate(value.monthDate, 'eight')}</Text>
        <Box>
          <Box mb={12}>
            <Space size={6}>
              <RotateArrowThirdIcon />
              <Text weight="medium">
                {formatMoney(value.drawdownsAmount, {parentheses: true})}
              </Text>
            </Space>
            <Text color="grey" size="xs" weight="medium">
              {maybePluralize(value.drawdownsCount, 'drawdown')}
            </Text>
          </Box>
          <Box mb={12}>
            <Space size={6}>
              <RotateArrowFourthIcon />
              <Text weight="medium" color="green">
                {formatMoney(value.repaymentsAmount)}
              </Text>
            </Space>
            <Text color="grey" size="xs" weight="medium">
              {maybePluralize(value.repaymentsCount, 'repayment')}
            </Text>
          </Box>
          <Text weight="bold">
            {formatMoney(Math.abs(value.totalDiff), {
              parentheses: value.diffType === 'drawdown',
            })}
          </Text>
        </Box>
      </div>
      <Divider />
      <div className={styles.tooltipBlock}>
        <Box mb={24}>
          <Text weight="bold" mb={2} color={positivePrev ? 'green' : undefined}>
            {formatMoney(Math.abs(value.prevAmount), {parentheses: positive})}
          </Text>
          <Text color="grey" size="xs" weight="medium">
            {label} {value.month}
          </Text>
        </Box>
        <IconStat
          row
          align="right"
          icon={role.manager ? <CashflowIcon /> : <MoneyIcon />}
          value={
            <Text color={positive ? 'green' : undefined}>
              {formatMoney(Math.abs(value.amount), {parentheses: positive})}
            </Text>
          }
          label={label}
        />
      </div>
    </>
  )
}

const columnPadding = 60
const labelTextHeight = 13
const labelTextRepaymentOffset = 2
const labelTextDrawdownOffset = 4
const tooltipChartId = 'forecastChart'

const options = {
  theme: CHART_THEME,
  smooth: true,
  xAxis: false,
  tooltip: false,
  limitInPlot: false,
  legend: false,
  appendPadding: [columnPadding, 0, columnPadding, 0],
  xField: 'month',
  yField: 'amount',
  lineStyle: {
    stroke: COLOR.coal,
    lineWidth: 4,
  },
}
